import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Plus, Heart, Sparkles, X } from 'lucide-react';
import { 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  addDays, 
  addMonths, 
  subMonths,
  format,
  isSameMonth,
  isSameDay,
  isToday,
  parseISO
} from 'date-fns';

// Pet Care Tips Component
const PetCareTip = () => {
  const tips = [
    {
      tip: "A wagging tail doesn't always mean a happy dog. Watch for body language to understand their true mood.",
      icon: "🐕"
    },
    {
      tip: "Cats sleep 12-16 hours a day. That's perfectly normal and helps them conserve energy!",
      icon: "😺"
    },
    {
      tip: "Always provide fresh water for pets. Change it at least twice daily to keep them healthy and hydrated.",
      icon: "💧"
    },
    {
      tip: "Regular play time reduces stress and behavioral issues in both cats and dogs.",
      icon: "🎾"
    },
    {
      tip: "Pets thrive on routine. Try to keep feeding and walking times consistent.",
      icon: "⏰"
    },
    {
      tip: "A calm greeting helps anxious pets feel safe. Speak softly and move slowly at first.",
      icon: "🤗"
    },
    {
      tip: "Mental stimulation is just as important as physical exercise for pets.",
      icon: "🧠"
    },
    {
      tip: "Pay attention to eating habits. Changes can be early signs of health issues.",
      icon: "🍽️"
    },
    {
      tip: "Create a quiet, comfortable space where pets can retreat when they need alone time.",
      icon: "🏠"
    },
    {
      tip: "Positive reinforcement works better than punishment for training and behavior.",
      icon: "⭐"
    },
    {
      tip: "Regular brushing reduces shedding and helps you spot skin issues early.",
      icon: "✨"
    },
    {
      tip: "Older pets need extra patience and may require more frequent bathroom breaks.",
      icon: "👴"
    },
    {
      tip: "Each pet has unique preferences. What works for one may not work for another.",
      icon: "💝"
    },
    {
      tip: "A tired pet is a happy pet! Make sure they get enough exercise for their age and breed.",
      icon: "🏃"
    },
    {
      tip: "Trust is earned slowly. Be patient and consistent with nervous or shy pets.",
      icon: "💕"
    }
  ];

  const [currentTip, setCurrentTip] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTip((prev) => (prev + 1) % tips.length);
    }, 10000); // Change tip every 10 seconds

    return () => clearInterval(interval);
  }, [tips.length]);

  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-pastel-blue/30 via-pastel-purple/30 to-pastel-pink/30 rounded-2xl border border-brand-brown/10">
      <div className="flex items-start space-x-3">
        <div className="text-3xl flex-shrink-0 mt-1">{tips[currentTip].icon}</div>
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <Heart className="h-4 w-4 text-brand-brown/60" />
            <span className="text-xs font-semibold text-brand-brown/70 uppercase tracking-wide">
              Pet Care Tip
            </span>
          </div>
          <p className="text-brand-brown leading-relaxed">
            {tips[currentTip].tip}
          </p>
        </div>
      </div>
      <div className="flex justify-center mt-3 space-x-1">
        {tips.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentTip(index)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              index === currentTip 
                ? 'w-6 bg-brand-brown/60' 
                : 'w-1.5 bg-brand-brown/20 hover:bg-brand-brown/40'
            }`}
            aria-label={`Go to tip ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const Calendar = ({ appointments = [], selectedDate, onDateSelect, onDateDoubleClick, onAddTodo, onDeleteAppointment }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showDateMenu, setShowDateMenu] = useState(false);
  const [menuDate, setMenuDate] = useState(null);

  const serviceTypeColors = {
    daycare: 'bg-service-daycare',
    sitting: 'bg-service-sitting',
    walking: 'bg-service-walking',
    checkin: 'bg-service-checkin',
  };

  // Get appointments for a specific date
  const getAppointmentsForDate = (date) => {
    return appointments.filter(apt => {
      const aptDate = typeof apt.date === 'string' ? parseISO(apt.date) : apt.date;
      return isSameDay(aptDate, date);
    });
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days = [];
    let day = startDate;

    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }

    return days;
  };

  const days = generateCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleDateClick = (day) => {
    onDateSelect && onDateSelect(day);
    setMenuDate(day);
    setShowDateMenu(true);
  };

  return (
    <div className="w-full">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-brand-brown">
          {format(currentMonth, 'MMMM yyyy')}
        </h3>
        <div className="flex space-x-2">
          <button
            onClick={prevMonth}
            className="p-2 rounded-xl hover:bg-pastel-pink/30 transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-brand-brown" />
          </button>
          <button
            onClick={nextMonth}
            className="p-2 rounded-xl hover:bg-pastel-blue/30 transition-colors"
          >
            <ChevronRight className="h-5 w-5 text-brand-brown" />
          </button>
        </div>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {weekDays.map(day => (
          <div key={day} className="text-center text-sm font-semibold text-brand-brown/70 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2">
        {days.map((day, index) => {
          const dayAppointments = getAppointmentsForDate(day);
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const isCurrentDay = isToday(day);

          return (
            <button
              key={index}
              onClick={() => handleDateClick(day)}
              onDoubleClick={() => onDateDoubleClick && onDateDoubleClick(day)}
              className={`
                relative min-h-[80px] p-2 rounded-xl border-2 transition-all duration-200
                ${isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                ${isSelected ? 'border-pastel-blue shadow-md' : 'border-transparent hover:border-pastel-pink'}
                ${isCurrentDay ? 'ring-2 ring-pastel-purple' : ''}
              `}
              title="Click to see options or double-click to add appointment"
            >
              <div className={`
                text-sm font-medium mb-1
                ${isCurrentMonth ? 'text-brand-brown' : 'text-brand-brown/30'}
                ${isCurrentDay ? 'font-bold' : ''}
              `}>
                {format(day, 'd')}
              </div>

              {/* Appointment Indicators */}
              {dayAppointments.length > 0 && (
                <div className="space-y-1">
                  {dayAppointments.slice(0, 3).map((apt, idx) => (
                    <div
                      key={idx}
                      className={`
                        h-1.5 rounded-full
                        ${serviceTypeColors[apt.serviceType] || 'bg-gray-300'}
                      `}
                      title={`${apt.petName} - ${apt.serviceType}`}
                    />
                  ))}
                  {dayAppointments.length > 3 && (
                    <div className="text-xs text-brand-brown/60 mt-1">
                      +{dayAppointments.length - 3} more
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected Date Info */}
      {selectedDate && !showDateMenu && (
        <div className="mt-6 p-4 bg-pastel-blue/20 rounded-xl">
          <h4 className="font-semibold text-brand-brown mb-2">
            {format(selectedDate, 'EEEE, MMMM d, yyyy')}
          </h4>
          {getAppointmentsForDate(selectedDate).length > 0 ? (
            <div className="space-y-2">
              {getAppointmentsForDate(selectedDate).map((apt, idx) => (
                <div key={idx} className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${serviceTypeColors[apt.serviceType]}`} />
                  <span className="text-sm text-brand-brown">
                    {apt.time} - {apt.petName} ({apt.serviceType})
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-brand-brown/60">No appointments scheduled</p>
          )}
        </div>
      )}

      {/* Date Action Menu */}
      {showDateMenu && menuDate && (
        <div 
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" 
          onClick={() => setShowDateMenu(false)}
        >
          <div 
            className="bg-white rounded-2xl p-6 m-4 max-w-sm w-full shadow-2xl" 
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-brand-brown mb-1">
              {format(menuDate, 'MMMM d, yyyy')}
            </h3>
            <p className="text-sm text-brand-brown/60 mb-6">
              {format(menuDate, 'EEEE')}
            </p>

            {/* Existing Appointments */}
            {getAppointmentsForDate(menuDate).length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-brand-brown mb-3">
                  {getAppointmentsForDate(menuDate).length} appointment(s) scheduled
                </p>
                <div className="space-y-2">
                  {getAppointmentsForDate(menuDate).map((apt) => (
                    <div 
                      key={apt.id}
                      className="flex items-center justify-between p-3 bg-pastel-blue/10 rounded-lg hover:bg-pastel-blue/20 transition-colors"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-brand-brown">
                          {apt.time} - {apt.petName}
                        </p>
                        <p className="text-xs text-brand-brown/60">
                          {apt.clientName} • {apt.serviceType}
                        </p>
                      </div>
                      {onDeleteAppointment && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete appointment for ${apt.petName}?`)) {
                              onDeleteAppointment(apt.id);
                              setShowDateMenu(false);
                            }
                          }}
                          className="ml-3 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete appointment"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={() => {
                  onDateDoubleClick && onDateDoubleClick(menuDate);
                  setShowDateMenu(false);
                }}
                className="w-full btn-primary py-3 flex items-center justify-center space-x-2"
              >
                <CalendarIcon className="h-5 w-5" />
                <span>Create Appointment</span>
              </button>

              {onAddTodo && (
                <button
                  onClick={() => {
                    onAddTodo(menuDate);
                    setShowDateMenu(false);
                  }}
                  className="w-full btn-secondary py-3 flex items-center justify-center space-x-2"
                >
                  <Plus className="h-5 w-5" />
                  <span>Add To-Do Item</span>
                </button>
              )}

              <button
                onClick={() => setShowDateMenu(false)}
                className="w-full text-brand-brown/60 hover:text-brand-brown py-2 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pet Care Tip - Moved to bottom */}
      <div className="mt-8">
        <PetCareTip />
      </div>
    </div>
  );
};

export default Calendar;
